import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useQuery, useModal } from '@panel/commons/hooks';
import { currenciesBySite, site } from '@panel/commons/helpers';
import Cookies from 'js-cookie';
import { Step4Module1Selectors, Step4Module1Operations } from '../../../ducks/step4Module1';
import { Step1Module1Operations, Step1Module1Selectors } from '../../../ducks/step1Module1';
import { getPresentationInfo, Step4Module3Selectors } from '../../../ducks/step4Module3';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { Modal } from '../../Modals';
import { ErrorFreeProduct } from './modals/ErrorFreeProduct';
import { sendEvent, PUBLICATION_ABANDON, track } from '../../../utils/trackingEvent';
import { Body } from './styles';
import { PlanSelector } from './sections/plans/planSelector';
import { PlanAvailable } from './sections/plans/planAvailable';
import { PlanCurrent } from './sections/plans/planCurrent';
import { CardSelector } from './sections/cardSelector';
import { PaymentMethods } from './sections/paymentMethods';
import { InfoCard } from './sections/infoCard';
import { updateLastStep } from '../../../ducks/stepper';
import { getEcommerceUser } from '../../../ducks/step4Module2';
import { getWithUpgrade } from '../../../utils/misc';

const Step4Module1 = WrapperContainer(({ setId, setHideSubmit }) => {
  const [withUpgrade, setWithUpgrade] = useState(false);
  const { contactInfo } = useSelector(Step4Module3Selectors.getPostingPreviewInfo);
  const { postingStatuses, operationTypeId } = useSelector(Step1Module1Selectors.getInfoEvents);
  const {
    products,
    availablePlans,
    currentPlan,
    freeAvailable,
    userTypeId,
    selectedOperationTypeId,
    eligibleForUpgrade,
    creditsCompleted
  } = useSelector(Step4Module1Selectors.getStep4Module1State);
  const {
    propertySubTypes,
    propertyTypes,
    formValues: { selectedPropertyTypeId, selectedPropertySubTypeId }
  } = useSelector(Step1Module1Selectors.getStep1Module1State);
  const { isShowing, toggle } = useModal();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { push } = useHistory();
  const dispatch = useDispatch();
  const { symbol } = Object.values(currenciesBySite[site.id])[0];

  const currentPlanId = eligibleForUpgrade && currentPlan ? currentPlan.publication_plan_id : '';
  const planName = currentPlan ? currentPlan.publication_areas[0].name : '';
  const successFlow = site.isEcuador && contactInfo && contactInfo.phone.length > 0;
  const propertyObj = { list: propertyTypes, id: selectedPropertyTypeId };
  const subPropertyObj = { list: propertySubTypes, id: selectedPropertySubTypeId };
  const forbiddenInfoCardSites = ['PLEC', 'CAPA', 'ZPAR'].includes(site.id);

  const notifyAbandon = () => {
    if (postingStatuses.includes('DRAFT')) {
      track(PUBLICATION_ABANDON, operationTypeId, postingId);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingStatuses]);

  useEffect(() => {
    if (eligibleForUpgrade && products.length > 0 && !withUpgrade) {
      setWithUpgrade(getWithUpgrade(products));
    }
  }, [products, eligibleForUpgrade, withUpgrade]);

  useEffect(() => {
    setId('step4module1Form');
  }, [setId]);

  useEffect(() => {
    dispatch(Step4Module1Operations.getCredits(postingId));
    const payload = {
      module: 'OPERATION',
      step: 'STEP_MAIN',
      postingId
    };
    dispatch(Step1Module1Operations.getFormValues(payload));
    if (site.isEcuador) {
      dispatch(getPresentationInfo(postingId));
      dispatch(getEcommerceUser());
    }
  }, [dispatch, postingId]);

  useEffect(() => {
    if (creditsCompleted) {
      dispatch(Step4Module1Operations.getProductsPosting(currentPlanId, postingId));
    }
  }, [dispatch, currentPlanId, creditsCompleted, postingId]);

  useEffect(() => {
    setHideSubmit(availablePlans.length === 0);
  }, [availablePlans, setHideSubmit]);

  useEffect(() => {
    const selected = availablePlans.find(p => p.selected);
    if (selected) {
      const type = selected.publicationPlan.publication_areas[0].publication_type;
      Cookies.set(
        `selectedPlan${postingId}`,
        `${selected.publicationPlan.publication_plan_id}_${
          currentPlan ? currentPlan.publication_plan_id : '0'
        }_${type.toLowerCase()}`,
        {
          expires: 1
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePlans, currentPlan, postingId]);

  return (
    <Body data-repa='step4module1'>
      {isShowing && (
        <Modal data-repa='step4module1-modal-errorFree'>
          <ErrorFreeProduct postingId={postingId} closeModal={toggle} />
        </Modal>
      )}
      {withUpgrade && (
        <PlanCurrent
          data-repa='step4module1-plansArea'
          availablePlans={availablePlans}
          products={products}
          currentPlan={currentPlan}
        />
      )}
      {!withUpgrade && (
        <PlanSelector
          data-repa='step4module1-plansArea'
          availablePlans={availablePlans}
          freeAvailable={freeAvailable}
          products={products}
          currentPlan={currentPlan}
        />
      )}
      <CardSelector
        data-repa='step4module1-productsArea'
        symbol={symbol}
        availablePlans={availablePlans}
        products={products}
        selectedOperationTypeId={selectedOperationTypeId}
        userTypeId={userTypeId}
        siteId={site.id}
        isUpgrade={withUpgrade}
        successFlow={successFlow}
        freeAvailable={freeAvailable}
        planName={planName}
      />
      {!forbiddenInfoCardSites && <InfoCard />}
      {withUpgrade && (
        <>
          <PaymentMethods />
          <PlanAvailable
            data-repa='step4module1-plansArea'
            availablePlans={availablePlans}
            products={products}
            currentPlan={currentPlan}
          />
        </>
      )}
      <form
        data-repa='step4module1-form'
        id='step4module1Form'
        onSubmit={e => {
          const { nativeEvent } = e;
          const saveAndExit = nativeEvent.submitter && nativeEvent.submitter.id === 'saveAndExit';
          const filterSelectedPlan = () => {
            const selectedPlan = availablePlans.filter(p => p.selected === true);
            if (selectedPlan[0].publicationPlan.name === 'Freemium Venta') {
              return 'Gratis';
            }
            return selectedPlan[0].publicationPlan.publication_areas[0].name;
          };

          sendEvent(
            'Planes Modulo1',
            saveAndExit ? 'Guardar y salir' : `Continuar-Plan ${filterSelectedPlan()}`,
            operationTypeId,
            propertyObj,
            subPropertyObj
          );
          if (saveAndExit) {
            notifyAbandon();
          }
          dispatch(updateLastStep('4', '1'));
          const next = !saveAndExit
            ? {
                pathname: '/publicador/planes/vistaprevia',
                search: `?postingId=${postingId}`
              }
            : {
                pathname: '/bum'
              };
          push(next);
        }}
      />
    </Body>
  );
});

export { Step4Module1 };
